import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import { appVersion, getBaseUrl } from '../axios/constants';
import apiEndpoints from './const';
import type {
    IActivityRequestParams,
    IAppleLoginPayload,
    IAppleLoginResponse,
    IAuthLoginResponse,
    ICreateTokenPayload,
    IGoogleLoginInitPayload,
    IGoogleLoginInitResponse,
    IProfile,
    IProfileActivityRes,
    IRefreshTokenPayload,
    IResendSmsPayload,
    IResendSmsResponse,
    ISendSmsPayload,
    ISendSmsResponse,
    ITokenPayload,
    ITokenResponse,
    IUpgradeTokenPayload,
    IUpgradeTokenResponse,
    IValidatePhonePayload,
    IValidatePhoneResponse,
    IValidateTokenPayload,
    IValidateTokenResponse,
    IProfileSettings,
    ISetConsentPayload,
    IGetConsentRes,
    IGetConsentPayload,
    IProfileForm,
} from './type';
import { deleteMenuAuth, getMenuAuth, IMenuAuth, setMenuAuth } from '../../service/session/menu';
import { deleteAuth, getAuth, setAuth } from '../../service/session/auth';
import { timeConversion } from '../../utility/k_time';
import axiosInstance from '../axios';
import { menuApiUrlPrefix } from '..';
import { transformActivityResponse, transformUpgradeTokenResponse } from './transformer';

export default class AuthManager {
    private static instance: AuthManager;

    private readonly axiosInstance: AxiosInstance;

    private authToken: ITokenResponse = {
        JWTToken: '',
        refresh: '',
    };

    private menuAuthToken: IMenuAuth = {
        access: '',
        refresh: '',
    };

    public static getInstance() {
        if (!this.instance) {
            this.instance = new AuthManager();
        }

        return this.instance;
    }

    constructor() {
        console.log('AuthManager | constructor');
        const instance = axios.create({
            baseURL: getBaseUrl().http,
            headers: {
                Accepted: 'application/json',
                'Content-Type': 'application/json',
                'Qlub-App-Version': appVersion,
            },
            timeout: 5000,
        });

        axiosRetry(instance, {
            retries: 3,
            shouldResetTimeout: true,
            retryCondition: (error) => {
                return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.code === 'ECONNABORTED';
            },
        });

        instance.interceptors.request.use(async (config) => {
            console.debug(`%cRequest:${config.method}:${config.url}`, 'color: #f9d71c', config.data);
            // @ts-ignore
            if (!config.metadata) {
                // @ts-ignore
                config.metadata = {
                    et: Date.now(),
                };
            } else {
                // @ts-ignore
                config.metadata.et = Date.now();
            }
            const authToken = getAuth();
            if (authToken.JWTToken && config.headers) {
                config.headers['Qlub-User-Auth'] = `Bearer ${authToken.JWTToken}`;
            }
            return config;
        });

        instance.interceptors.response.use(
            (response) => {
                // @ts-ignore
                const diff = response?.config?.metadata?.et ? Date.now() - (response?.config?.metadata?.et || 0) : null;
                console.debug(
                    `%cResponse:${response?.config?.method}:${response?.config?.url}`,
                    'color: #f967a0',
                    { res: response.data },
                    diff ? timeConversion(diff) : '',
                );
                return response;
            },
            async (error) => {
                const { response, config: request } = error;
                // menu server
                if (request?.url?.startsWith(menuApiUrlPrefix) && response?.status === 401) {
                    try {
                        if (request.url === apiEndpoints.menu.refresh) {
                            throw new Error('refresh error');
                        }
                        const token = await this.menuRefresh();
                        if (token && request.headers) {
                            request.headers.Authorization = `Bearer ${token}`;
                        }
                    } catch (e) {
                        console.warn('menuRefresh', e);
                        const token = await this.menuAuth(true);
                        if (token && request.headers) {
                            request.headers.Authorization = `Bearer ${token}`;
                        }
                        if (request.url === apiEndpoints.menu.refresh) {
                            return Promise.reject(e);
                        }
                    }
                    // resending failed request with updated tokens
                    return instance.request(request);
                }
                // core edge
                if (request?.url !== apiEndpoints.auth.refreshToken && [401].includes(response?.status)) {
                    try {
                        const token = await this.refresh();

                        if (token && request.headers) {
                            request.headers['Qlub-User-Auth'] = `Bearer ${token}`;
                        }
                    } catch (e: any) {
                        if ((e?.response?.status || e?.status) === 401) {
                            this.profileDeleteAuth();
                        }
                        return Promise.reject(e);
                    }

                    // resending failed request with updated tokens
                    return instance.request(request);
                }
                return Promise.reject(error);
            },
        );

        this.axiosInstance = instance;
        this.menuAuthToken = getMenuAuth();
        this.authToken = getAuth();
    }

    public sendSms = (payload: ISendSmsPayload) => {
        return this.axiosInstance
            .post<ISendSmsResponse>(apiEndpoints.auth.sendSms, payload, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                return res.data;
            });
    };

    public resendSms = (payload: IResendSmsPayload) => {
        return this.axiosInstance
            .post<IResendSmsResponse>(apiEndpoints.auth.resendSms, payload, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                return res.data;
            });
    };

    public getUserToken = (payload: ITokenPayload) => {
        return axiosInstance.Post<ITokenResponse, ITokenPayload>(apiEndpoints.auth.createToken, payload, {
            'axios-retry': {
                retries: 3,
            },
        });
    };

    public createToken = (payload: ICreateTokenPayload) => {
        return this.axiosInstance
            .post<ITokenResponse>(apiEndpoints.auth.createToken, payload, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                return {
                    ...res.data,
                    isAuthorized: res.data.profile && !res.data.profile.isGuest,
                };
            });
    };

    public appleLogin = (payload: IAppleLoginPayload) => {
        return this.axiosInstance
            .post<IAppleLoginResponse>(apiEndpoints.auth.apple, payload, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                return res.data;
            });
    };

    public googleLoginInit = (payload: IGoogleLoginInitPayload) => {
        return this.axiosInstance
            .post<IGoogleLoginInitResponse>(apiEndpoints.auth.googleLoginInit, payload, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                return res.data;
            });
    };

    public getProfile = () => {
        return this.axiosInstance
            .get<IProfile>(apiEndpoints.profile.profile, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                return res.data;
            });
    };

    public setProfile = (payload: IProfileForm) => {
        return this.axiosInstance.post<IProfile>(apiEndpoints.profile.profile, payload).then((res) => {
            return res;
        });
    };

    public validateToken = (payload: IValidateTokenPayload) => {
        return this.axiosInstance
            .post<IValidateTokenResponse>(apiEndpoints.auth.validateToken, payload, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                return res.data;
            });
    };

    public refreshToken = (payload: IRefreshTokenPayload) => {
        return this.axiosInstance
            .post<ITokenResponse>(apiEndpoints.auth.refreshToken, payload, {
                'axios-retry': {
                    retries: 3,
                    retryCondition: (err: any) => {
                        return (
                            axiosRetry.isNetworkError(err) ||
                            (err && err.response?.data?.code >= 500 && err.response?.data?.code < 600)
                        );
                    },
                },
            })
            .then((res) => {
                return res.data;
            });
    };

    public upgradeToken = (payload: IUpgradeTokenPayload) => {
        return this.axiosInstance
            .post<IUpgradeTokenResponse>(apiEndpoints.auth.upgradeToken, payload, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                return transformUpgradeTokenResponse(res.data);
            });
    };

    public refreshPromise: Promise<string> | null = null;

    public refresh(): Promise<string> {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        if (!this.authToken.refresh) {
            this.profileDeleteAuth();
            return Promise.reject(new Error('Broken refresh token in auth'));
        }

        const promise = this.refreshToken({ refresh: this.authToken.refresh })
            .then((res) => {
                this.authToken.JWTToken = res?.JWTToken || '';
                if (res?.refresh) {
                    this.authToken.refresh = res?.refresh || '';
                }
                setAuth({
                    JWTToken: this.authToken.JWTToken,
                    refresh: this.authToken.refresh,
                    isAuthorized: getAuth().isAuthorized,
                });
                return this.authToken.JWTToken;
            })
            .catch((err) => {
                this.profileDeleteAuth();
                throw err;
            })
            .finally(() => {
                this.refreshPromise = null;
            });

        this.refreshPromise = promise;
        return promise;
    }

    private profileDeleteAuth() {
        this.authToken = {
            JWTToken: '',
            refresh: '',
        };
        deleteAuth();
    }

    public menuAuth(force?: boolean): Promise<string> {
        if (this.menuAuthToken.access && !force) {
            return Promise.resolve(this.menuAuthToken.access);
        }
        return this.axiosInstance.get<IAuthLoginResponse>(apiEndpoints.menu.login).then((res) => {
            this.menuAuthToken.access = res.data?.data?.accessToken || '';
            this.menuAuthToken.refresh = res.data?.data?.refreshToken || '';
            setMenuAuth({
                access: this.menuAuthToken.access,
                refresh: this.menuAuthToken.refresh,
            });
            return this.menuAuthToken.access;
        });
    }

    public menuRefresh(): Promise<string> {
        if (!this.menuAuthToken.refresh) {
            this.menuDeleteAuth();
            return Promise.reject(new Error('Broken refresh token in menu'));
        }
        return this.axiosInstance
            .post<IAuthLoginResponse>(apiEndpoints.menu.refresh, {
                refreshToken: this.menuAuthToken.refresh,
            })
            .then((res) => {
                this.menuAuthToken.access = res.data?.data?.accessToken || '';
                if (res.data?.data?.refreshToken) {
                    this.menuAuthToken.refresh = res.data?.data?.refreshToken || '';
                }
                setMenuAuth({
                    access: this.menuAuthToken.access,
                    refresh: this.menuAuthToken.refresh,
                });
                return this.menuAuthToken.access;
            });
    }

    public validatePhone = (payload: IValidatePhonePayload) => {
        return this.axiosInstance.post<IValidatePhoneResponse>(apiEndpoints.auth.validatePhone, payload).then((res) => {
            return res.data;
        });
    };

    private menuDeleteAuth() {
        this.menuAuthToken = {
            access: '',
            refresh: '',
        };
        deleteMenuAuth();
    }

    public setProfileSettings = (payload: IProfileSettings) => {
        return this.axiosInstance.post(apiEndpoints.profile.settings, payload).then((res) => {
            return res.data;
        });
    };

    public getProfileSettings = () => {
        return this.axiosInstance.get<IProfileSettings>(apiEndpoints.profile.settings).then((res) => {
            return res.data;
        });
    };

    public getActivities = (params: IActivityRequestParams) => {
        return this.axiosInstance
            .get<IProfileActivityRes>(apiEndpoints.profile.activities, {
                params,
            })
            .then((res) => {
                return res.data;
            })
            .then((data) => {
                return transformActivityResponse(data);
            });
    };

    public getConsent = (payload: IGetConsentPayload) => {
        return this.axiosInstance
            .get<IGetConsentRes>(apiEndpoints.profile.consent, {
                params: payload,
            })
            .then((res) => {
                return res.data;
            });
    };

    public setConsent = (payload: ISetConsentPayload) => {
        return this.axiosInstance.post(apiEndpoints.profile.consent, payload).then((res) => {
            return res.data;
        });
    };
}
